/**
 * PeriodPicker — the ONE period control both 2.3b surfaces render (the Quotas
 * card on Settings → Sales and the Forecast tab on Reports), so a manager
 * setting a target and a manager reading attainment name the window the same
 * way. Kind is the .k-segmented capsule (the Operations Tasks precedent:
 * role=group + aria-pressed, with aria-selected carried redundantly because
 * klaxiora.css keys the active fill on it); previous/next are quiet buttons
 * with an explicit label. The period itself is salesPeriod.ts arithmetic, so
 * every value this control emits is a first day the server accepts.
 */
import type { SalesPeriodKind } from '../../../../api/types'
import { type SalesPeriod, periodLabel, periodRangeLabel, shiftPeriod, switchPeriodKind } from './salesPeriod'

const KINDS: readonly { kind: SalesPeriodKind; label: string }[] = [
  { kind: 'month', label: 'Month' },
  { kind: 'quarter', label: 'Quarter' },
]

export function PeriodPicker({ period, onChange, disabled }: {
  period: SalesPeriod
  onChange: (next: SalesPeriod) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="k-segmented" role="group" aria-label="Period kind">
        {KINDS.map(k => (
          <button key={k.kind} type="button" disabled={disabled}
            aria-pressed={period.kind === k.kind} aria-selected={period.kind === k.kind}
            onClick={() => onChange(switchPeriodKind(period, k.kind))}>{k.label}</button>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <button type="button" className="k-btn-quiet" aria-label="Previous period" disabled={disabled}
          onClick={() => onChange(shiftPeriod(period, -1))}>‹</button>
        <span className="text-13 font-semibold text-ink-primary min-w-[4.5rem] text-center" aria-live="polite">
          {periodLabel(period)}
        </span>
        <button type="button" className="k-btn-quiet" aria-label="Next period" disabled={disabled}
          onClick={() => onChange(shiftPeriod(period, 1))}>›</button>
      </div>
      <span className="text-12 text-ink-muted">{periodRangeLabel(period)}</span>
    </div>
  )
}
